/**
 * Zustand Store for Shift Management
 */

import { create } from 'zustand';
import { api } from '../lib/api';
import type { ShiftSchedule, CreateShiftFormData, UpdateShiftFormData } from '../types/shifts';

interface ShiftsState {
  shifts: ShiftSchedule[];
  loading: boolean;
  error: string | null;
  fetchShifts: (isActive?: boolean) => Promise<void>;
  createShift: (data: CreateShiftFormData) => Promise<ShiftSchedule>;
  updateShift: (id: string, data: UpdateShiftFormData) => Promise<ShiftSchedule>;
  deleteShift: (id: string) => Promise<void>;
  clearError: () => void;
}

export const useShiftsStore = create<ShiftsState>((set, get) => ({
  shifts: [],
  loading: false,
  error: null,

  fetchShifts: async (isActive) => {
    set({ loading: true, error: null });
    try {
      const shifts = await api.getShifts(isActive);
      set({ shifts, loading: false });
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
      throw error;
    }
  },

  createShift: async (data) => {
    set({ loading: true, error: null });
    try {
      // Convert HH:MM to HH:MM:SS for API
      const shiftData = {
        name: data.name,
        start_time: `${data.start_time}:00`,
        end_time: `${data.end_time}:00`,
        description: data.description,
      };
      const newShift = await api.createShift(shiftData);
      
      // Add to store
      set(state => ({
        shifts: [...state.shifts, newShift],
        loading: false,
      }));
      
      return newShift;
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
      throw error;
    }
  },

  updateShift: async (id, data) => {
    set({ loading: true, error: null });
    try {
      // Convert HH:MM to HH:MM:SS if present
      const shiftData: any = { ...data };
      if (data.start_time) shiftData.start_time = `${data.start_time}:00`;
      if (data.end_time) shiftData.end_time = `${data.end_time}:00`;
      
      const updatedShift = await api.updateShift(id, shiftData);
      
      // Update in store
      set(state => ({
        shifts: state.shifts.map(s => s.id === id ? updatedShift : s),
        loading: false,
      }));
      
      return updatedShift;
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
      throw error;
    }
  },

  deleteShift: async (id) => {
    set({ loading: true, error: null });
    try {
      await api.deleteShift(id);
      
      // Remove from store (or mark inactive)
      set(state => ({
        shifts: state.shifts.filter(s => s.id !== id),
        loading: false,
      }));
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
      throw error;
    }
  },

  clearError: () => set({ error: null }),
}));
